import { Check } from "lucide-react";
import { testAttrs } from "@/utils/testAttrs";

interface Step {
  id: number;
  label: string;
}

interface BasketStepperProps {
  steps: Step[];
  currentStep: number;
  paymentPage?: boolean;
}

export function BasketStepper({
  steps,
  currentStep,
  paymentPage = false,
}: BasketStepperProps) {
  return (
    <div style={{ backgroundColor: "var(--surface-container-accent-blue-tint)" }} {...testAttrs({ "data-testid": "basket-stepper" })}>
      <div className="max-w-4xl mx-auto px-2 sm:px-4">
        <div className="flex flex-row items-stretch justify-center gap-1 sm:gap-4">
          {steps.map((step, index) => {
            const isCompleted = step.id < currentStep;
            const isCurrent = step.id === currentStep;
            const isLast = index === steps.length - 1;
            const displayLabel =
              paymentPage && step.id === 1 ? "Summary" : step.label;

            return (
              <div key={step.id} className="flex items-center"
                {...testAttrs({
                  "data-testid": "basket-step",
                  "data-step-id": String(step.id),
                  "data-step-active": String(isCurrent),
                  "data-step-completed": String(isCompleted),
                })}
              >
                <div
                  className={`relative flex items-center py-2 sm:py-3 border-b-2 ${
                    isCurrent ? "border-dominos-blue" : "border-transparent"
                  }`}
                >
                  <span
                    className={`rounded-full flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 leading-none shrink-0 text-[11px] sm:text-sm ${
                      isCurrent
                        ? "bg-dominos-red-bright text-white"
                        : "bg-gray-300 text-gray-600"
                    }`}
                    {...testAttrs({ "data-testid": "basket-step-circle" })}
                  >
                    {isCompleted ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : step.id}
                  </span>

                  <span
                    className={`ml-1 sm:ml-2 nav-font text-[12px] sm:text-3xl uppercase font-normal whitespace-nowrap ${
                      isCurrent
                        ? "text-dominos-red-bright"
                        : "text-dominos-gray-medium"
                    }`}
                    {...testAttrs({ "data-testid": "basket-step-label" })}
                  >
                    {displayLabel}
                  </span>

                  {isCurrent && (
                    <div
                      className="absolute z-50 left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 pointer-events-none"
                      style={{
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderBottom: "8px solid hsl(var(--dominos-blue))",
                      }}
                    />
                  )}
                </div>

                {!isLast && (
                  <div className="w-3 h-3 sm:w-6 sm:h-6 ml-1 sm:ml-6 text-gray-500 shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-3 h-3 sm:w-6 sm:h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
